import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import DataTable from 'react-data-table-component'
import { getAllData, deleteIdData } from '../../lib/resourcecenterview'
import { useAuth } from '../../_helper/Auth/AuthProvider'
import { useRoleBasedUrl } from '../../Hooks/useRoleBasedUrl'
import { useLayout } from '../../Hooks/useLayout'
import { getResourceColumns } from '../../Data/Resource/ResourceListData'

export const ResourceList = ({ slug, isStudent = true }) => {
	const [resources, setResources] = useState([])
	const [filterText, setFilterText] = useState('')
	const [loading, setLoading] = useState(true)
	const [isDarkMode, setIsDarkMode] = useState(false)
	const { user } = useAuth()
	const role = useRoleBasedUrl()
	const layout = useLayout()

	async function GetData() {
		try {
			setLoading(true)
			const data = await getAllData()
			setResources(data)
		} catch (error) {
			console.error('Error fetching resource data:', error)
		} finally {
			setLoading(false)
		}
	}

	useEffect(() => {
		if (user) {
			GetData()
		}
	}, [user, slug])

	useEffect(() => {
		const checkDarkMode = () => {
			setIsDarkMode(document.body.classList.contains('dark-only'))
		}
		checkDarkMode()
		const observer = new MutationObserver(checkDarkMode)
		observer.observe(document.body, {
			attributes: true,
			attributeFilter: ['class'],
		})
		return () => observer.disconnect()
	}, [])

	const handleDelete = async (id) => {
		const choice = window.confirm('Are you sure you want to delete this resource?')
		if (choice) {
			try {
				await deleteIdData(id)
				await GetData()
			} catch (error) {
				console.error('Error deleting resource:', error)
				alert('An error occurred while deleting the resource.')
			}
		}
	}

	const filteredResources = resources.filter(
		(item) =>
			item.howuse?.toLowerCase().includes(filterText.toLowerCase()) ||
			item.pname?.toLowerCase().includes(filterText.toLowerCase()) ||
			item.name?.toLowerCase().includes(filterText.toLowerCase()) ||
			item.surname?.toLowerCase().includes(filterText.toLowerCase()) ||
			item.grade?.toString().includes(filterText)
	)

	const columns = getResourceColumns(isStudent, role, layout, slug, handleDelete, isDarkMode)

	const customStyles = {
		headCells: {
			style: {
				fontSize: '14px',
				fontWeight: 'bold',
				textTransform: 'uppercase',
				backgroundColor: isDarkMode ? '#2a2d35' : '#f8f9fa',
				color: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : 'inherit',
				borderBottom: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : '#dee2e6'}`,
			}
		},
		rows: {
			style: {
				minHeight: '50px',
				fontSize: '14px',
				backgroundColor: isDarkMode ? '#262932' : '#fff',
				'&:nth-of-type(odd)': {
					backgroundColor: isDarkMode ? '#1d1e26' : '#f8f9fa',
				},
				'&:hover': {
					backgroundColor: isDarkMode ? '#2a2d35' : '#e9ecef',
				},
			}
		},
		cells: {
			style: {
				paddingTop: '12px',
				paddingBottom: '12px',
				fontSize: '14px',
				color: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : 'inherit',
				backgroundColor: isDarkMode ? '#262932' : '#fff',
			}
		},
		pagination: {
			style: {
				backgroundColor: isDarkMode ? '#262932' : '#fff',
				color: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : 'inherit',
			}
		}
	}

	return (
		<div className="col-12">
			<div
				className="card shadow-sm mb-4"
				style={{
					borderRadius: '8px',
					backgroundColor: isDarkMode ? '#262932' : '#fff',
				}}
			>
				<div
					className="card-header border-bottom d-flex justify-content-between align-items-center"
					style={{ backgroundColor: isDarkMode ? '#262932' : '#fff' }}
				>
					<div className="d-flex align-items-center">
						<button
							type="button"
							className="btn btn-link p-0 me-2"
							data-bs-toggle="collapse"
							data-bs-target="#collapseResourceList"
							aria-expanded="false"
							aria-controls="collapseResourceList"
						>
							<i className="fa fa-chevron-down"></i>
						</button>
						<h5
							className="mb-0 d-inline-block"
							style={{
								color: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : 'inherit',
							}}
						>
							Resource Center
						</h5>
					</div>
				</div>
				<div className="collapse" id="collapseResourceList">
					<div
						className="card-body"
						style={{ backgroundColor: isDarkMode ? '#262932' : '#fff' }}
					>
						<div className="d-flex justify-content-between align-items-center mb-3">
							<h6
								className="mb-0"
								style={{
									color: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : 'inherit',
								}}
							>
								Educational resources and materials
							</h6>
							<div className="d-flex gap-2 align-items-center">
								<input
									type="text"
									placeholder="Search resources..."
									className="form-control"
									style={{
										maxWidth: '300px',
										backgroundColor: isDarkMode ? '#2a2d35' : '#fff',
										color: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : 'inherit',
										borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : '#dee2e6',
									}}
									value={filterText}
									onChange={(e) => setFilterText(e.target.value)}
								/>
								{!isStudent && (
									<Link
										to={`${process.env.PUBLIC_URL}/${role}/instructor-resource-edit/${slug}/Add/0/${layout}`}
										className="btn btn-primary btn-sm"
									>
										<i className="fa fa-plus me-2"></i>
										Add Resource
									</Link>
								)}
							</div>
						</div>

						{loading ? (
							<div className="text-center p-4">
								<div className="spinner-border" role="status">
									<span className="visually-hidden">Loading...</span>
								</div>
								<p
									className="mt-2"
									style={{
										color: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : 'inherit',
									}}
								>
									Loading resources...
								</p>
							</div>
						) : (
							<DataTable
								columns={columns}
								data={filteredResources}
								pagination
								paginationPerPage={25}
								paginationRowsPerPageOptions={[10, 25, 50, 100]}
								highlightOnHover
								striped
								responsive
								dense
								persistTableHead
								customStyles={customStyles}
								theme={isDarkMode ? 'dark' : 'default'}
								noDataComponent={
									<div
										className="text-center p-4"
										style={{
											color: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : 'inherit',
										}}
									>
										<p>No resources found</p>
									</div>
								}
							/>
						)}
					</div>
				</div>
			</div>
		</div>
	)
}
